import type { MediaDimensions } from "@auger/core"
import { assetKind } from "@auger/core/fields"
import type { AssetFieldsFragment } from "@auger/core/gql/types"
import {
  assetFileUrl,
  assetKey,
  deriveFilename,
  readPresignResponse,
  UPLOAD_BUCKET_NAME,
  uploadError,
} from "@auger/storage/rules"
import { netlifyFunctionClient } from "@/common/functions"
import type { ComposableDeps } from "@/composables/types"
import { getAuger } from "@/init/auger"
import { errorMessage } from "@/utils/errors"
import { extractMediaDimensions } from "@/utils/files"

// useAssets Composable ========================================================

/** The record an upload is filed under: its type and id pick the S3 prefix. */
export interface UploadRecord {
  id: number
  type?: string | null
}

interface UploadOptions {
  /** Progress callback, 0-100. */
  onProgress?: (percent: number) => void
  record?: UploadRecord | null
  /** Leave reporting to the caller (the upload tray). */
  silent?: boolean
}

interface UploadUrlOptions {
  /** Override the filename (default: derived from the URL). */
  filename?: string
  /** The asset's `metadata.origin` (default "url_upload"). */
  origin?: string
  record?: UploadRecord | null
}

interface UploadSource {
  name: string
  size: number
  type: string
  origin: string
  dimensions?: MediaDimensions | null
  record?: UploadRecord | null
  /** PUTs the file to the presigned URL. */
  put: (presignedUrl: string) => Promise<void>
}

/**
 * Composable for uploading files to S3 as asset rows.
 *
 * This composable deals with the assets table only, not the record_attachments
 * junction table: attaching an asset to a record goes through core's relation
 * writes.
 *
 * The interface's half of an upload: reading the file, asking the Netlify
 * function for a presigned URL, and the PUT to S3. The asset row's lifecycle
 * (created incomplete, marked complete, removed on failure) is core's
 * `assets.upload`.
 */
export function useAssets(deps: ComposableDeps) {
  const { client, toast } = deps

  function upload(source: UploadSource): Promise<AssetFieldsFragment> {
    const { name, size, type, origin, dimensions, record, put } = source
    return getAuger(client).assets.upload({
      file: { name, size, type },
      origin,
      dimensions,
      presign: async () => {
        const response = await netlifyFunctionClient().post("aws-s3-upload-asset", {
          filename: name,
          size,
          type,
          record_type: record?.type,
          record_id: record?.id,
        })
        return readPresignResponse(response.data)
      },
      put: (ticket) => put(ticket.putUrl),
    })
  }

  /**
   * Upload a file to S3 and create its asset row. Media dimensions are read
   * first; a file that won't report them uploads without.
   */
  async function uploadFile(file: File, opts: UploadOptions = {}): Promise<AssetFieldsFragment> {
    const { onProgress, record, silent = false } = opts
    try {
      const refused = uploadError({ mimeType: file.type, size: file.size })
      if (refused) throw new Error(refused)
      const dimensions = await extractMediaDimensions(file).catch((err: Error) => {
        console.warn("Could not extract media dimensions:", err.message)
        return null
      })
      const asset = await upload({
        name: file.name,
        size: file.size,
        type: file.type,
        origin: "manual_upload",
        dimensions,
        record,
        put: (presignedUrl) => putToS3(presignedUrl, file, onProgress),
      })
      if (!silent) toast?.success?.("File uploaded")
      return asset
    } catch (err) {
      if (!silent) toast?.error?.(`Upload failed: ${errorMessage(err)}`)
      throw err
    }
  }

  /**
   * Fetch an image URL and upload it to S3 as an asset row. Rejects a URL that
   * isn't an image.
   */
  async function uploadUrl(url: string, opts: UploadUrlOptions = {}): Promise<AssetFieldsFragment> {
    const { filename, origin = "url_upload", record } = opts
    try {
      const resp = await fetch(url)
      if (!resp.ok) throw new Error(`Failed to fetch image: ${resp.status}`)
      const blob = await resp.blob()
      if (!blob.type.startsWith("image/")) throw new Error("URL does not point to an image")

      const asset = await upload({
        name: filename || deriveFilename(url, blob.type),
        size: blob.size,
        type: blob.type,
        origin,
        record,
        put: async (presignedUrl) => {
          const put = await fetch(presignedUrl, {
            method: "PUT",
            headers: { "Content-Type": blob.type },
            body: blob,
          })
          if (!put.ok) throw new Error(`S3 upload failed: ${put.status}`)
        },
      })
      toast?.success?.("Image uploaded")
      return asset
    } catch (err) {
      toast?.error?.(`Upload failed: ${errorMessage(err)}`)
      throw err
    }
  }

  return { uploadFile, uploadUrl }
}

/** PUT a file to S3 through a presigned URL, reporting progress as it goes. */
function putToS3(presignedUrl: string, file: File, onProgress?: (percent: number) => void) {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    if (onProgress) {
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100))
      })
    }

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`S3 upload failed with status ${xhr.status}`))
      }
    })

    xhr.addEventListener("error", () => reject(new Error("Network error during upload")))
    xhr.addEventListener("abort", () => reject(new Error("Upload aborted")))

    xhr.open("PUT", presignedUrl)
    xhr.setRequestHeader("Content-Type", file.type)
    xhr.send(file)
  })
}

// File presentation ===========================================================

const KIND_ICONS: Record<string, string> = {
  image: "file-image",
  video: "file-video",
  audio: "file-audio",
  pdf: "file-pdf",
  spreadsheet: "file-excel",
  presentation: "file-powerpoint",
  document: "file-word",
  text: "file-alt",
  other: "file",
}

/** The Font Awesome icon for a file's MIME type. */
export function fileIcon(mimeType: string | null | undefined) {
  return KIND_ICONS[assetKind(mimeType)]!
}

/** The part of an asset row that locates its file. */
type AssetLike = Partial<Pick<AssetFieldsFragment, "url" | "metadata">>

/**
 * The asset's canonical URL: its S3 key through the asset host, else the stored
 * URL. Safe to persist; servedUrl is the one to render.
 */
export function getAssetUrl(asset: AssetLike | null | undefined) {
  return assetFileUrl(asset)
}

/**
 * The URL to render a stored file from. Locally, asset-host URLs go through
 * the dev-only proxy: CloudFront's signed cookies are scoped to .heavybit.com
 * and absent in dev without a fresh staging login, so direct loads 403.
 */
export function servedUrl(url: string | null | undefined) {
  if (!url || !import.meta.env.DEV) return url ?? null
  const key = assetKey(url)
  if (key == null) return url
  return `/.netlify/functions/dev-asset-proxy?key=${encodeURIComponent(key)}`
}

/** The URL to render an asset row's file from. Only uploaded files need the
 *  dev proxy; synced files from the other bucket load straight from the host. */
export function assetSrc(asset: AssetLike | null | undefined) {
  const url = getAssetUrl(asset)
  const bucket = (asset?.metadata as { s3_bucket?: string } | null)?.s3_bucket
  return bucket && bucket !== UPLOAD_BUCKET_NAME ? url : servedUrl(url)
}
